import {
  Box,
  Button,
  IconButton,
  Typography,
  Stack,
  Paper,
  Fade,
  Avatar,
  Tooltip,
  Chip,
  Switch,
  Dialog,
  DialogTitle,
  DialogActions,
  FormGroup,
  FormControlLabel
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonIcon from "@mui/icons-material/Person";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { adminServices } from "../../services/AllServices";
import { ADMIN_ROLES } from "../../utils/constants";
import errorHandler from "../../utils/errorHandler";
import { useNavigate } from "react-router-dom";
import { EditOutlined } from "@mui/icons-material";
import { Loader } from "../../components/CustomLoader";
import { fDate } from "../../utils/formatTime";
import { Helmet } from "react-helmet-async";
import "./AdminManagement.css";

export default function AdminManagementList() {
  const { user } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [show, setShow] = useState(false);
  const [warning, setWarning] = useState("");
  const [currentStatus, setCurrentStatus] = useState(null);
  const [statusId, setStatusId] = useState("");

  const fetchAdmins = async () => {
    try {
      setIsLoading(true);
      const res = await adminServices.getAdmins({ searchItem: "", pageNumber: 1, pageSize: 100 });
      if (res.status === 200) {
        setData(res?.data?.data?.result || []);
      }
    } catch (err) {
      errorHandler(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, [refresh]);

  const deleteAdmin = async (id) => {
    Swal.fire({
      text: "Are you sure you want to delete this admin?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#00a1da",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await adminServices.deleteAdmin({ id });
          if (res?.status === 200) {
            toast.success(res?.data?.message);
            setRefresh(!refresh);
          }
        } catch (error) {
          errorHandler(error);
        }
      }
    });
  };

  const toggleStatus = async (id, currentStatus) => {
    try {
      const newStatus = currentStatus === 1 ? 0 : 1;
      const res = await adminServices.changeStatus({
        id,
        status: newStatus,
      });

      if (res.status === 200) {
        toast.success(res?.data?.message);
        setRefresh(!refresh);
      }
    } catch (err) {
      errorHandler(err);
    }
  };

  const columns = [
    {
      name: "S.No.",
      cell: (row, index) => index + 1,
      width: "80px"
    },
    {
      name: "Name",
      sortable: true,
      selector: (row) => row.name,
      cell: (row) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'white' }}>
          {row.name}
        </Typography>
      )
    },
    {
      name: "Email",
      sortable: true,
      selector: (row) => row.email,
    },
    {
      name: "Role",
      sortable: true,
      selector: (row) => row.role,
      cell: (row) => (
        <Chip 
          label={row.role} 
          size="small"
          sx={{ 
            bgcolor: row.role === 'SuperAdmin' ? 'error.main' : row.role === 'Mgt' ? 'primary.main' : 'success.main',
            color: 'white',
            fontWeight: 'bold'
          }}
        />
      )
    },
    {
      name: "Status",
      cell: (row) => (
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={row.status === 1}
                onChange={() => {
                  setShow(true);
                  setCurrentStatus(row.status);
                  setStatusId(row._id);
                  setWarning(
                    `Are you sure you want to ${row.status === 1 ? "deactivate" : "activate"} this admin?`
                  );
                }}
                disabled={row.role === 'SuperAdmin'}
              />
            }
            label={row.status === 1 ? "Active" : "Inactive"}
          />
        </FormGroup>
      ),
    },
    {
      name: "Created At",
      sortable: true,
      selector: (row) => fDate(row.createdAt),
    },
    {
      name: "Action",
      center: true,
      cell: (row) => (
        <Stack direction="row" spacing={1}>
          <Tooltip title="Edit">
            <IconButton
              color="primary"
              onClick={() => navigate(`/admin-management/edit/${row?._id}`)}
              disabled={row.role === 'SuperAdmin'}
            >
              <EditOutlined />
            </IconButton>
          </Tooltip>
          <Tooltip title="Change Password">
            <IconButton
              color="primary"
              onClick={() => navigate(`/admin-management/change-password/${row?._id}`)}
              disabled={row.role === 'SuperAdmin'}
            >
              <VpnKeyIcon />
            </IconButton>
          </Tooltip>
          {user?.role !== ADMIN_ROLES.JUNIOR && (
            <Tooltip title="Delete">
              <IconButton 
                color="primary" 
                onClick={() => deleteAdmin(row._id)}
                disabled={row.role === 'SuperAdmin'}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      ),
    },
  ];

  const customStyles = {
    table: {
      style: {
        backgroundColor: 'transparent',
      },
    },
    header: {
      style: {
        backgroundColor: 'transparent',
        color: 'white',
      },
    },
    headRow: {
      style: {
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
      },
    },
    headCells: {
      style: {
        color: 'white',
        fontSize: '14px',
        fontWeight: 'bold',
      },
    },
    rows: {
      style: {
        backgroundColor: 'transparent',
        color: 'white',
        '&:not(:last-of-type)': {
          borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
        },
      },
      highlightOnHoverStyle: {
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        transitionDuration: '0.15s',
        transitionProperty: 'background-color',
        borderBottomColor: 'rgba(255, 255, 255, 0.05)',
        outline: 'none',
      },
    },
    pagination: {
      style: {
        backgroundColor: 'transparent',
        color: 'white',
      },
    },
  };

  return (
    <Box className="admin-management-container">
      <Helmet>
        <title>Admin Management</title>
      </Helmet>
      <Fade in timeout={500}>
        <Stack spacing={4}>
          <Paper elevation={0} className="admin-header-paper">
            <Box className="admin-header-content">
              <Box className="admin-header-left">
                <Avatar className="admin-header-avatar">
                  <PersonIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" className="admin-header-title">
                    Admin Management
                  </Typography>
                  <Typography variant="body1" className="admin-header-subtitle">
                    Manage system administrators and roles
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="contained"
                className="admin-add-button"
                onClick={() => navigate("/admin-management/add")}
              >
                + Add Admin
              </Button>
            </Box>
          </Paper>

          <Paper elevation={0} className="admin-content-paper">
            {isLoading ? (
              <Box className="admin-loading-container">
                <Loader />
              </Box>
            ) : (
              <DataTable
                columns={columns}
                data={data}
                pagination
                customStyles={customStyles}
                noDataComponent={
                  <Box className="admin-empty-container">
                    <Typography className="admin-empty-title">No admins found</Typography>
                  </Box>
                }
              />
            )}
          </Paper>
        </Stack>
      </Fade>

      {/* Status Change Dialog */}
      <Dialog open={show} onClose={() => setShow(false)}>
        <DialogTitle sx={{ color: 'black' }}>{warning}</DialogTitle>
        <DialogActions>
          <Button
            onClick={() => {
              toggleStatus(statusId, currentStatus);
              setShow(false);
            }}
            variant="contained"
            color="primary"
          >
            Yes
          </Button>
          <Button
            onClick={() => setShow(false)}
            variant="outlined"
            color="primary"
          >
            No
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
